import { useMemo, useState, type ReactElement } from "react";
import moment from "moment/moment";
import useLocalStorage from "use-local-storage";
import PropertyCard from "./PropertyCard.tsx";
// @ts-expect-error pure js
import { handleAPIError } from "../../utils";
// @ts-expect-error pure js
import IsLoading from "../UI/IsLoading";
// @ts-expect-error pure js
import useTenants from "../../hooks/use-tenants";
// @ts-expect-error pure js
import useProfileBailleurs from "../../hooks/use-profile-bailleurs";
// @ts-expect-error pure js
import { getTotalLoyer } from "../../utils/properties.js";
import { naturalCompare } from "../../utils/sort";
// @ts-expect-error pure js
import useAuthenticatedAxios from "../../hooks/use-authenticated-axios";
// @ts-expect-error pure js
import DocumentDownloader from "../UI/DocumentDownloader";
import Text from "../UI/Text";
import Button from "../UI/Button";
import EmptyState from "../UI/EmptyState";
import {
  IconArchive,
  IconFileExport,
  IconHome,
  IconPlus,
} from "@tabler/icons-react";
import FilterBar from "../UI/FilterBar/FilterBar.tsx";
import FilterSection, {
  FilterRangeSection,
  FilterSelectSection,
} from "../UI/FilterBar/FilterSection.tsx";
import type { Property } from "../../models/property";
import type { ProfileBailleur } from "../Profile/types";
import type { PropertyFolder } from "../../models/propertyFolder";
import type { Tenant } from "../../models/tenant";
import type { PropertiesAndFolders } from "./PropertyListShow";
import styles from "./PropertyCard.module.css";

const STATUS_OPTIONS = [
  { value: "vide", label: "Vide" },
  { value: "preavis", label: "Préavis" },
  { value: "loue", label: "Loué" },
  { value: "reloue", label: "Reloué" },
];

const GLI_OPTIONS = [
  { value: "avec", label: "Avec GLI" },
  { value: "sans", label: "Sans GLI" },
];

interface PropertyListProps {
  propertiesAndFolders: PropertiesAndFolders;
  onDuplicateProperty: (property: Property) => Promise<void>;
  onAdd?: () => void;
  addLabel?: string;
  isLoading: boolean;
}

export default function PropertyList({
  propertiesAndFolders,
  onDuplicateProperty,
  onAdd,
  addLabel,
  isLoading,
}: PropertyListProps) {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useLocalStorage<string[]>(
    "propertyList_statusFilter",
    [],
  );
  const [gliFilter, setGliFilter] = useLocalStorage<string[]>(
    "propertyList_gliFilter",
    [],
  );
  const [minRentFilter, setMinRentFilter] = useLocalStorage<string>(
    "propertyList_minRentFilter",
    "",
  );
  const [maxRentFilter, setMaxRentFilter] = useLocalStorage<string>(
    "propertyList_maxRentFilter",
    "",
  );
  const [bailleurFilter, setBailleurFilter] = useLocalStorage<string[]>(
    "propertyList_bailleurFilter",
    [],
  );
  const { tenants } = useTenants() as { tenants: Tenant[] };
  const { profileBailleurs } = useProfileBailleurs() as {
    profileBailleurs: ProfileBailleur[];
  };
  const axios = useAuthenticatedAxios();
  const [exportError, setExportError] = useState("");

  const bailleurOptions = useMemo(() => {
    const ownerIds = new Set<number>();
    const collectOwners = (props: Property[] | undefined) =>
      props?.forEach((prop) => ownerIds.add(prop.id_profile_nom_owner));
    const walkFolders = (folders: PropertyFolder[] | undefined) =>
      folders?.forEach((folder) => {
        collectOwners(folder.properties);
        walkFolders(folder.property_folders);
      });
    collectOwners(propertiesAndFolders?.properties);
    walkFolders(propertiesAndFolders?.property_folders);

    return [...(profileBailleurs ?? [])]
      .filter((pb) => ownerIds.has(pb.id))
      .sort((a, b) => a.nom_profile.localeCompare(b.nom_profile))
      .map((pb) => ({ value: String(pb.id), label: pb.nom_profile }));
  }, [profileBailleurs, propertiesAndFolders]);

  // Purge les valeurs stockées qui ne correspondent plus à une option
  // (profil supprimé, onglet sans bien de ce profil) pour éviter un filtre invisible.
  const activeBailleurFilter = useMemo(
    () =>
      bailleurFilter.filter((value) =>
        bailleurOptions.some((opt) => opt.value === value),
      ),
    [bailleurFilter, bailleurOptions],
  );

  function clearAllFilters() {
    setStatusFilter([]);
    setGliFilter([]);
    setMinRentFilter("");
    setMaxRentFilter("");
    setBailleurFilter([]);
  }

  const filteredData = useMemo(() => {
    const searchLower = search.toLowerCase();
    const hasActiveFilter =
      searchLower !== "" ||
      statusFilter.length > 0 ||
      gliFilter.length > 0 ||
      !!minRentFilter ||
      !!maxRentFilter ||
      activeBailleurFilter.length > 0;

    const matchesSearch = (property: Property) => {
      if (!searchLower) return true;
      return (
        property.nom?.toLowerCase().includes(searchLower) ||
        property.ville?.toLowerCase().includes(searchLower) ||
        property.rue?.toLowerCase().includes(searchLower)
      );
    };

    const matchesFilters = (property: Property) => {
      if (!matchesSearch(property)) return false;

      const locataireActuel = tenants.find(
        (ten) =>
          ten.id_bien === property.id &&
          ten.status === 1 &&
          moment().isBetween(ten.debut, ten.fin),
      );
      const preavis = tenants.find(
        (ten) =>
          ten.id_bien === property.id &&
          ten.status === 1 &&
          ten.preavis &&
          moment().isBetween(ten.debut, ten.fin),
      );
      const nextLocataire = tenants.find(
        (ten) =>
          ten.id_bien === property.id &&
          ten.status === 1 &&
          moment().isBefore(ten.debut),
      );

      if (statusFilter && statusFilter.length > 0) {
        let matchesStatus = false;
        if (statusFilter.includes("vide") && !locataireActuel) {
          matchesStatus = true;
        }
        if (statusFilter.includes("preavis") && preavis) {
          matchesStatus = true;
        }
        if (statusFilter.includes("loue") && locataireActuel && !preavis) {
          matchesStatus = true;
        }
        if (statusFilter.includes("reloue") && nextLocataire) {
          matchesStatus = true;
        }
        if (!matchesStatus) {
          return false;
        }
      }

      const baseRent = getTotalLoyer(property) || 0;

      if (minRentFilter && baseRent < Number(minRentFilter)) {
        return false;
      }
      if (maxRentFilter && baseRent > Number(maxRentFilter)) {
        return false;
      }

      if (gliFilter && gliFilter.length > 0) {
        const hasGli =
          !!property.gli ||
          [locataireActuel, nextLocataire].some(
            (tenant) =>
              tenant?.profiles?.some(
                (profile) => profile.caution_type === "GLI",
              ),
          );
        const wantsAvec = gliFilter.includes("avec");
        const wantsSans = gliFilter.includes("sans");
        if (wantsAvec && !wantsSans && !hasGli) return false;
        if (wantsSans && !wantsAvec && hasGli) return false;
      }

      return !(
        activeBailleurFilter.length > 0 &&
        !activeBailleurFilter.includes(String(property.id_profile_nom_owner))
      );
    };

    const filterFolders = (
      folders: PropertyFolder[] | undefined,
    ): PropertyFolder[] => {
      if (!folders || folders.length === 0) return [];
      return folders
        .map((folder) => ({
          ...folder,
          properties: (folder.properties ?? []).filter(matchesFilters),
          property_folders: filterFolders(folder.property_folders),
        }))
        .filter(
          (folder) =>
            !hasActiveFilter ||
            folder.properties.length > 0 ||
            folder.property_folders.length > 0,
        );
    };

    const getPropertyIdsInFolders = (
      folders: PropertyFolder[] | undefined,
    ): Set<number> => {
      const ids = new Set<number>();
      if (!folders || folders.length === 0) return ids;
      folders.forEach((folder) => {
        folder.properties?.forEach((prop) => ids.add(prop.id));
        getPropertyIdsInFolders(folder.property_folders).forEach((id) =>
          ids.add(id),
        );
      });
      return ids;
    };

    const propertyIdsInFolders = getPropertyIdsInFolders(
      propertiesAndFolders?.property_folders,
    );

    const rootProperties = (propertiesAndFolders?.properties || []).filter(
      (property) => !propertyIdsInFolders.has(property.id),
    );
    const filteredRootProperties = rootProperties.filter(matchesFilters);

    const filteredFolders = filterFolders(
      propertiesAndFolders?.property_folders,
    );

    return {
      properties: filteredRootProperties,
      property_folders: filteredFolders,
    };
  }, [
    propertiesAndFolders,
    search,
    statusFilter,
    minRentFilter,
    maxRentFilter,
    gliFilter,
    activeBailleurFilter,
    tenants,
  ]);

  const getFilteredPropertyIds = useMemo(() => {
    const extractIds = (folders: PropertyFolder[] | undefined): number[] => {
      const ids: number[] = [];
      if (!folders || folders.length === 0) return ids;
      folders.forEach((folder) => {
        folder.properties?.forEach((prop) => ids.push(prop.id));
        ids.push(...extractIds(folder.property_folders));
      });
      return ids;
    };

    const rootIds = (filteredData?.properties || []).map((p) => p.id);
    const folderIds = extractIds(filteredData?.property_folders);
    return [...rootIds, ...folderIds];
  }, [filteredData]);

  const listBiens: { name: string; element: ReactElement }[] = [];
  if (filteredData?.property_folders?.length > 0) {
    listBiens.push(
      ...filteredData.property_folders.map((propertyFolder) => ({
        name: propertyFolder.nom,
        element: (
          <PropertyCard
            propertyFolder={propertyFolder}
            key={`folder-${propertyFolder.id}`}
            onDuplicateProperty={onDuplicateProperty}
          />
        ),
      })),
    );
  }
  if (filteredData?.properties?.length > 0) {
    listBiens.push(
      ...filteredData.properties.map((property) => ({
        name: property.nom,
        element: (
          <PropertyCard
            property={property}
            key={`prop-${property.id}`}
            onDuplicateProperty={onDuplicateProperty}
          />
        ),
      })),
    );
  }

  const propertyElements = listBiens
    .sort((a, b) => naturalCompare(a.name, b.name))
    .map((e) => e.element);

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (statusFilter.length > 0) count++;
    if (gliFilter.length > 0) count++;
    if (minRentFilter || maxRentFilter) count++;
    if (activeBailleurFilter.length > 0) count++;
    return count;
  }, [
    statusFilter,
    gliFilter,
    minRentFilter,
    maxRentFilter,
    activeBailleurFilter,
  ]);

  if (isLoading) return <IsLoading />;

  const hasAnyBien =
    (propertiesAndFolders?.properties?.length ?? 0) > 0 ||
    (propertiesAndFolders?.property_folders?.length ?? 0) > 0;

  if (!hasAnyBien) {
    return onAdd ? (
      <EmptyState
        icon={IconHome}
        title="Aucun bien pour le moment"
        description="Ajoutez votre premier bien pour créer vos baux, suivre vos loyers et centraliser vos documents."
      >
        <Button icon={IconPlus} onClick={onAdd}>
          {addLabel}
        </Button>
      </EmptyState>
    ) : (
      <EmptyState
        icon={IconArchive}
        title="Aucun bien archivé"
        description="Les biens que vous archivez apparaîtront ici."
      />
    );
  }

  const leftActions = (
    <>
      {onAdd && (
        <Button icon={IconPlus} onClick={onAdd}>
          {addLabel}
        </Button>
      )}
      <DocumentDownloader>
        {(setSignedUrl: (url: string) => void) => (
          <Button
            variant="secondary"
            icon={IconFileExport}
            onClick={async () => {
              try {
                setExportError("");
                const response = await axios.post("/biens/export", {
                  propertyIds: getFilteredPropertyIds,
                });
                setSignedUrl(response.data);
              } catch (error) {
                setExportError(handleAPIError(error));
              }
            }}
          >
            Exporter
          </Button>
        )}
      </DocumentDownloader>
    </>
  );

  return (
    <>
      <FilterBar
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Rechercher un bien"
        activeFiltersCount={activeFiltersCount}
        onClearFilters={clearAllFilters}
        leftActions={leftActions}
      >
        <FilterSection
          title="Statut"
          options={STATUS_OPTIONS}
          values={statusFilter}
          onChange={setStatusFilter}
        />
        <FilterSelectSection
          title="Profil bailleur"
          options={bailleurOptions}
          values={activeBailleurFilter}
          onChange={setBailleurFilter}
        />
        <FilterSection
          title="GLI"
          options={GLI_OPTIONS}
          values={gliFilter}
          onChange={setGliFilter}
        />
        <FilterRangeSection
          title="Loyer"
          minLabel="Min"
          maxLabel="Max"
          unit="€"
          minValue={minRentFilter}
          maxValue={maxRentFilter}
          onMinChange={setMinRentFilter}
          onMaxChange={setMaxRentFilter}
          inputType="number"
        />
      </FilterBar>

      {exportError && (
        <Text
          as="div"
          variant="deci"
          color="danger"
          className={styles.exportError}
        >
          {exportError}
        </Text>
      )}

      {propertyElements.length > 0 ? (
        <div className={styles.cardGrid}>{propertyElements}</div>
      ) : (
        <Text as="div" color="muted" className={styles.emptyState}>
          Aucun bien trouvé
        </Text>
      )}
    </>
  );
}
